import React from 'react';
import { Link } from 'react-router-dom';
import { PublicLayout } from '../../components/layout';

const lastUpdated = 'August 22, 2026';

const addingSteps = [
  'Sign in to Class-Tech with a teacher account. Student and parent accounts cannot authorize the Zoom integration.',
  'Open Settings from the top bar in the teacher dashboard, then select the Integrations tab.',
  'In the Zoom Integration section, select Connect Zoom Account.',
  'Class-Tech redirects you to Zoom to review the requested permissions.',
  'Select Allow in Zoom to authorize Class-Tech for your teacher Zoom account.',
  'Zoom redirects you back to Class-Tech. The Zoom Integration section shows Connected when setup is complete.',
  'If the connection fails, review the Troubleshooting section on this page or contact support@class-tech.net.',
];

const removingSteps = [
  'Sign in to Zoom App Marketplace.',
  'Select Manage in the top-right area of Zoom Marketplace.',
  'Open Added Apps.',
  'Find Class-Tech in the list of added apps.',
  'Select Remove, choose a reason if Zoom asks for one, then confirm removal.',
];

const classTechDisconnectSteps = [
  'Sign in to Class-Tech with the connected teacher account.',
  'Open Settings from the top bar in the teacher dashboard, then select the Integrations tab.',
  'In the Zoom Integration section, select Disconnect Zoom.',
  'Class-Tech removes the stored OAuth tokens and marks the Zoom integration as disconnected.',
];

const usageRows = [
  {
    feature: 'Connect a Zoom account',
    useCase:
      'A teacher connects their own Zoom account so Class-Tech can create Zoom meetings under that teacher account.',
    prerequisites:
      'The user must have an active Class-Tech teacher account and a Zoom account that can authorize OAuth apps.',
    steps:
      'Open Settings from the top bar in the teacher dashboard, choose Integrations > Zoom, select Connect Zoom Account, review the Zoom permissions, and select Allow.',
  },
  {
    feature: 'Create a Zoom meeting for a class session',
    useCase:
      'A teacher schedules a class session and creates a Zoom meeting link without leaving Class-Tech.',
    prerequisites:
      'The teacher must be signed in, have Zoom connected, and have an existing class or session to schedule.',
    steps:
      'Open the teacher dashboard, create or edit a class session, choose Create a new Zoom meeting, then save the session.',
  },
  {
    feature: 'View meeting link and meeting details',
    useCase:
      'Teachers and assigned students use the stored meeting link to join the scheduled online class.',
    prerequisites:
      'A class session must already be created with a Zoom meeting link.',
    steps:
      'Open the class session details from the teacher or student dashboard and select the saved Zoom meeting link.',
  },
  {
    feature: 'Delete a Zoom meeting created by Class-Tech',
    useCase:
      'A teacher removes a class session and Class-Tech can remove the related Zoom meeting when the meeting was created through the integration.',
    prerequisites:
      'The session must have been created by the connected teacher through Class-Tech Zoom integration.',
    steps:
      'Open the class session, delete the session, and confirm the action when Class-Tech asks for confirmation.',
  },
  {
    feature: 'Track attendance from Zoom participation',
    useCase:
      'Class-Tech uses Zoom participant join and leave events to calculate attendance for a class session after the meeting ends.',
    prerequisites:
      'The Zoom meeting must be created through Class-Tech, students should join using a name or email that matches their Class-Tech student record, and Zoom event subscriptions must be active.',
    steps:
      'Students join the Zoom class, Zoom sends participant events, and Class-Tech finalizes attendance after the meeting ends.',
  },
  {
    feature: 'Retrieve cloud recording metadata',
    useCase:
      'When a teacher records a class to the Zoom cloud, Class-Tech can retrieve metadata for the completed recording.',
    prerequisites:
      'The teacher Zoom account must support cloud recording, the class meeting must be recorded to the cloud, and recording permission must be authorized.',
    steps:
      'Record the Zoom meeting to the cloud, end the meeting, wait for Zoom processing, then review the session record in Class-Tech.',
  },
  {
    feature: 'Use a manual meeting link',
    useCase:
      'Teachers who do not connect Zoom can still schedule online classes by pasting a meeting link manually.',
    prerequisites:
      'The teacher must already have a meeting URL from Zoom, Google Meet, or another online meeting provider.',
    steps:
      'Create a class session, choose manual link, paste the meeting URL, then save the session.',
  },
];

const troubleshootingItems = [
  {
    issue: 'The Zoom integration shows Needs reconnect.',
    fix: 'Reconnect Zoom from Settings > Integrations and approve the requested permissions again.',
  },
  {
    issue: 'Class-Tech cannot create a Zoom meeting.',
    fix: 'Confirm that the teacher connected Zoom and approved meeting:write:meeting. If the account was reauthorized with older permissions, disconnect and connect Zoom again.',
  },
  {
    issue: 'Attendance does not match a student.',
    fix: 'Ask the student to join Zoom using the same email address or display name used in the Class-Tech student record.',
  },
  {
    issue: 'Cloud recordings do not appear.',
    fix: 'Confirm that the meeting was recorded to the Zoom cloud and that cloud_recording:read:recording is authorized.',
  },
  {
    issue: 'The OAuth redirect does not complete.',
    fix: 'Use the public Class-Tech domain and make sure the Zoom redirect URL is https://class-tech.net/zoomOAuthCallback.',
  },
];

const scopeRows = [
  ['user:read:user', 'Identifies the Zoom account connected by the teacher.'],
  ['meeting:write:meeting', 'Creates scheduled Zoom meetings for teacher class sessions.'],
  ['meeting:read:meeting', 'Retrieves meeting details for meetings created through Class-Tech.'],
  ['meeting:delete:meeting', 'Deletes a related Zoom meeting when a teacher deletes a Class-Tech session.'],
  ['meeting:read:participant', 'Processes Zoom participant data for class attendance calculation.'],
  [
    'cloud_recording:read:recording',
    'Retrieves cloud recording metadata for completed class meetings when recordings are used.',
  ],
];

const ZoomDocumentation: React.FC = () => {
  return (
    <PublicLayout>
      <main className='bg-secondary-50 text-secondary-900 dark:bg-secondary-950 dark:text-white'>
        <section className='bg-white py-16 dark:bg-secondary-900'>
          <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
            <div className='max-w-3xl'>
              <p className='text-sm font-semibold uppercase tracking-wide text-primary-600 dark:text-primary-300'>
                Zoom App Documentation
              </p>
              <h1 className='mt-3 text-3xl font-bold text-secondary-950 dark:text-white md:text-5xl'>
                Class-Tech Zoom Integration Guide
              </h1>
              <p className='mt-5 text-lg leading-8 text-secondary-700 dark:text-secondary-200'>
                This guide explains how teachers add, use, troubleshoot, and
                remove the Class-Tech Zoom integration. The integration is
                available only to authenticated teacher users and is used for
                class meeting, attendance, and recording workflows.
              </p>
              <p className='mt-4 text-sm text-secondary-500 dark:text-secondary-400'>
                Last updated: {lastUpdated}
              </p>
              <div className='mt-8 flex flex-col gap-3 sm:flex-row'>
                <a
                  href='mailto:support@class-tech.net'
                  className='inline-flex min-h-11 items-center justify-center rounded-lg bg-primary-600 px-5 text-sm font-semibold text-white hover:bg-primary-700'
                >
                  Contact Support
                </a>
                <Link
                  to='/terms'
                  className='inline-flex min-h-11 items-center justify-center rounded-lg border border-secondary-300 px-5 text-sm font-semibold text-secondary-800 hover:bg-secondary-100 dark:border-secondary-700 dark:text-white dark:hover:bg-secondary-800'
                >
                  Terms of Use
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className='py-12'>
          <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
            <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
              <article className='rounded-lg border border-secondary-200 bg-white p-6 shadow-sm dark:border-secondary-800 dark:bg-secondary-900'>
                <h2 className='text-xl font-bold text-secondary-950 dark:text-white'>
                  Adding the App
                </h2>
                <p className='mt-3 leading-7 text-secondary-600 dark:text-secondary-300'>
                  Follow these steps to authorize Class-Tech for a teacher Zoom
                  account.
                </p>
                <ol className='mt-5 list-decimal space-y-2 pl-5 text-sm leading-7 text-secondary-700 dark:text-secondary-200'>
                  {addingSteps.map(item => (
                    <li key={item}>{item}</li>
                  ))}
                </ol>
              </article>

              <article className='rounded-lg border border-secondary-200 bg-white p-6 shadow-sm dark:border-secondary-800 dark:bg-secondary-900'>
                <h2 className='text-xl font-bold text-secondary-950 dark:text-white'>
                  Removing the App
                </h2>
                <p className='mt-3 leading-7 text-secondary-600 dark:text-secondary-300'>
                  Teachers can disconnect Zoom from Class-Tech or remove the app
                  from Zoom Marketplace.
                </p>
                <h3 className='mt-5 text-sm font-semibold uppercase tracking-wide text-secondary-500 dark:text-secondary-400'>
                  Remove from Zoom Marketplace
                </h3>
                <ol className='mt-3 list-decimal space-y-2 pl-5 text-sm leading-7 text-secondary-700 dark:text-secondary-200'>
                  {removingSteps.map(item => (
                    <li key={item}>{item}</li>
                  ))}
                </ol>
                <h3 className='mt-6 text-sm font-semibold uppercase tracking-wide text-secondary-500 dark:text-secondary-400'>
                  Disconnect inside Class-Tech
                </h3>
                <ol className='mt-3 list-decimal space-y-2 pl-5 text-sm leading-7 text-secondary-700 dark:text-secondary-200'>
                  {classTechDisconnectSteps.map(item => (
                    <li key={item}>{item}</li>
                  ))}
                </ol>
                <p className='mt-5 text-sm leading-7 text-secondary-700 dark:text-secondary-200'>
                  After removal or disconnect, Class-Tech can no longer create
                  Zoom meetings for that teacher until Zoom is connected again.
                  Existing class sessions, attendance records, and meeting
                  history may remain as educational records. To request deletion
                  of integration-related records, contact support@class-tech.net.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section className='bg-white py-12 dark:bg-secondary-900'>
          <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
            <div className='max-w-3xl'>
              <h2 className='text-2xl font-bold text-secondary-950 dark:text-white'>
                Usage
              </h2>
              <p className='mt-4 leading-8 text-secondary-700 dark:text-secondary-200'>
                Each Zoom feature below is available only to authenticated
                Class-Tech teachers. Students and parents do not authorize Zoom
                and cannot create Zoom meetings through Class-Tech.
              </p>
              <p className='mt-3 leading-8 text-secondary-700 dark:text-secondary-200'>
                Teacher settings are consolidated in one Settings page. Platform
                settings, account settings, and Zoom integrations are separate
                tabs inside that page.
              </p>
            </div>
            <div className='mt-8 overflow-hidden rounded-lg border border-secondary-200 dark:border-secondary-800'>
              <table className='min-w-full divide-y divide-secondary-200 text-left text-sm dark:divide-secondary-800'>
                <thead className='bg-secondary-100 dark:bg-secondary-800'>
                  <tr>
                    <th className='px-4 py-3 font-semibold'>Feature</th>
                    <th className='px-4 py-3 font-semibold'>Use Case</th>
                    <th className='px-4 py-3 font-semibold'>Prerequisites</th>
                    <th className='px-4 py-3 font-semibold'>How to Use</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-secondary-200 bg-white dark:divide-secondary-800 dark:bg-secondary-900'>
                  {usageRows.map(row => (
                    <tr key={row.feature}>
                      <td className='px-4 py-4 font-semibold text-secondary-950 dark:text-white'>
                        {row.feature}
                      </td>
                      <td className='px-4 py-4 leading-7 text-secondary-700 dark:text-secondary-200'>
                        {row.useCase}
                      </td>
                      <td className='px-4 py-4 leading-7 text-secondary-700 dark:text-secondary-200'>
                        {row.prerequisites}
                      </td>
                      <td className='px-4 py-4 leading-7 text-secondary-700 dark:text-secondary-200'>
                        {row.steps}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className='py-12'>
          <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
            <div className='grid grid-cols-1 gap-8 lg:grid-cols-3'>
              <div className='lg:col-span-2'>
                <h2 className='text-2xl font-bold text-secondary-950 dark:text-white'>
                  Troubleshooting
                </h2>
                <div className='mt-6 space-y-4'>
                  {troubleshootingItems.map(item => (
                    <article
                      key={item.issue}
                      className='rounded-lg border border-secondary-200 bg-white p-5 dark:border-secondary-800 dark:bg-secondary-900'
                    >
                      <h3 className='font-semibold text-secondary-950 dark:text-white'>
                        {item.issue}
                      </h3>
                      <p className='mt-2 text-sm leading-7 text-secondary-700 dark:text-secondary-200'>
                        {item.fix}
                      </p>
                    </article>
                  ))}
                </div>
              </div>
              <aside className='rounded-lg bg-white p-6 shadow-sm dark:bg-secondary-900'>
                <h2 className='text-xl font-bold text-secondary-950 dark:text-white'>
                  Support
                </h2>
                <p className='mt-4 text-sm leading-7 text-secondary-700 dark:text-secondary-200'>
                  Email support@class-tech.net with the teacher account email,
                  session title, approximate date, and a short description of
                  the issue.
                </p>
                <p className='mt-4 text-sm leading-7 text-secondary-700 dark:text-secondary-200'>
                  Support hours: Sunday to Thursday, 9:00 AM to 6:00 PM Egypt
                  time. First response target: within 2 business days.
                </p>
                <Link
                  to='/support'
                  className='mt-5 inline-flex min-h-11 items-center justify-center rounded-lg bg-primary-600 px-5 text-sm font-semibold text-white hover:bg-primary-700'
                >
                  Support Center
                </Link>
              </aside>
            </div>
          </div>
        </section>

        <section className='bg-white py-12 dark:bg-secondary-900'>
          <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
            <div className='grid grid-cols-1 gap-8 lg:grid-cols-3'>
              <div className='lg:col-span-2'>
                <h2 className='text-2xl font-bold text-secondary-950 dark:text-white'>
                  Permissions Used
                </h2>
                <p className='mt-4 leading-8 text-secondary-700 dark:text-secondary-200'>
                  Class-Tech requests the minimum Zoom permissions needed for
                  teacher-authorized class workflows: connecting the teacher
                  Zoom account, creating and managing class meetings,
                  processing attendance, and retrieving cloud recording
                  metadata when recordings are used.
                </p>
                <div className='mt-6 overflow-hidden rounded-lg border border-secondary-200 dark:border-secondary-800'>
                  <table className='min-w-full divide-y divide-secondary-200 text-left text-sm dark:divide-secondary-800'>
                    <thead className='bg-secondary-100 dark:bg-secondary-800'>
                      <tr>
                        <th className='px-4 py-3 font-semibold'>Scope</th>
                        <th className='px-4 py-3 font-semibold'>Purpose</th>
                      </tr>
                    </thead>
                    <tbody className='divide-y divide-secondary-200 bg-white dark:divide-secondary-800 dark:bg-secondary-900'>
                      {scopeRows.map(([scope, purpose]) => (
                        <tr key={scope}>
                          <td className='px-4 py-3 font-mono text-xs text-primary-700 dark:text-primary-300'>
                            {scope}
                          </td>
                          <td className='px-4 py-3 text-secondary-700 dark:text-secondary-200'>
                            {purpose}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <aside className='rounded-lg bg-secondary-50 p-6 dark:bg-secondary-800'>
                <h3 className='font-bold text-secondary-950 dark:text-white'>
                  Review Links
                </h3>
                <div className='mt-4 space-y-3'>
                  <Link
                    to='/privacy'
                    className='block rounded-lg bg-white px-4 py-3 text-sm font-semibold text-primary-700 hover:text-primary-900 dark:bg-secondary-900 dark:text-primary-300'
                  >
                    Privacy Policy
                  </Link>
                  <Link
                    to='/terms'
                    className='block rounded-lg bg-white px-4 py-3 text-sm font-semibold text-primary-700 hover:text-primary-900 dark:bg-secondary-900 dark:text-primary-300'
                  >
                    Terms of Use
                  </Link>
                  <Link
                    to='/support'
                    className='block rounded-lg bg-white px-4 py-3 text-sm font-semibold text-primary-700 hover:text-primary-900 dark:bg-secondary-900 dark:text-primary-300'
                  >
                    Support Center
                  </Link>
                  <Link
                    to='/contact#contact-form'
                    className='block rounded-lg bg-white px-4 py-3 text-sm font-semibold text-primary-700 hover:text-primary-900 dark:bg-secondary-900 dark:text-primary-300'
                  >
                    Contact Form
                  </Link>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>
    </PublicLayout>
  );
};

export default ZoomDocumentation;
